import React, { useEffect, useState } from 'react';
import { Copy, Link2, Loader2, Mail, MessageSquare, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const CHANNELS = [['email', 'Email', Mail], ['sms', 'Text (SMS)', MessageSquare], ['link', 'Copy link', Link2]];

// Staff sends the IVORY Admission Consent to the patient's phone: one-use link, valid 72 hours
export const SendConsentLinkModal = ({ patient, onClose }) => {
  const [channel, setChannel] = useState('email');
  const [email, setEmail] = useState(patient?.email || '');
  const [phone, setPhone] = useState(patient?.cellPhone || patient?.phone || '');
  const [requireDob, setRequireDob] = useState(true);
  const [sms, setSms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState('');
  useEffect(() => { api.get('/consent-sign/config').then(r => setSms(r.data.sms)).catch(() => {}); }, []);
  const ready = channel === 'email' ? email.includes('@') : channel === 'sms' ? sms && phone.replace(/\D/g, '').length >= 10 : true;
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post('/consent-sign/send', { patientId: patient.id, channel, email: channel === 'email' ? email : undefined, phone: channel === 'sms' ? phone : undefined, requireDob });
      setLink(r.data.link);
      if (channel === 'link') await navigator.clipboard?.writeText(r.data.link).catch(() => {});
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create the link');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" data-testid="consent-link-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="bg-gray-900 p-4 rounded-t-xl flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#d4af37]">Send Consent for Signature</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="consent-link-close"><XCircle className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-gray-600">Patient: <strong>{patient.firstName} {patient.lastName}</strong>. They get a secure link to sign the IVORY Admission Consent on their phone. It works once and expires in 72 hours. The signed copy is saved to the patient's Application Packages.</p>
          <div className="grid grid-cols-3 gap-2" data-testid="consent-channel">
            {CHANNELS.map(([id, label, Icon]) => (
              <button key={id} type="button" onClick={() => { setChannel(id); setLink(''); }} data-testid={`consent-channel-${id}`}
                className={`py-2 rounded-lg border text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${channel === id ? 'bg-gray-900 text-[#d4af37] border-gray-900' : 'text-gray-700 hover:bg-gray-50'}`}>
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
          {channel === 'email' && <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="patient@email.com" className="w-full px-3 py-2 border rounded-lg" data-testid="consent-email-input" />}
          {channel === 'sms' && (sms
            ? <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Mobile number" className="w-full px-3 py-2 border rounded-lg" data-testid="consent-phone-input" />
            : <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2" data-testid="consent-sms-not-connected">Text messages are not connected yet (RingCentral texting number and app keys needed). Use Email or Copy link for now.</p>)}
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={requireDob} onChange={e => setRequireDob(e.target.checked)} data-testid="consent-require-dob" />
            Patient must enter their date of birth before signing
          </label>
          {link && (
            <div className="flex gap-2" data-testid="consent-link-result">
              <input readOnly value={link} className="flex-1 px-2 py-1.5 border rounded text-xs bg-gray-50" data-testid="consent-link-value" />
              <button onClick={() => navigator.clipboard?.writeText(link).then(() => toast.success('Link copied'))} className="px-3 rounded bg-gray-100 hover:bg-gray-200" data-testid="consent-link-copy"><Copy className="w-4 h-4" /></button>
            </div>
          )}
          <button onClick={send} disabled={busy || !ready} data-testid="consent-link-send"
            className="w-full py-2.5 rounded-full bg-[#d4af37] text-gray-900 font-semibold disabled:bg-gray-300 disabled:text-gray-600 flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {channel === 'email' ? 'Email Link' : channel === 'sms' ? 'Text Link' : 'Create & Copy Link'}
          </button>
        </div>
      </div>
    </div>
  );
};
